
import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { callSmartApi } from '@/utils/supabaseApi';
import { useToast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';
import { 
  CopilotConversation, 
  CopilotMessage, 
  CopilotAttachment 
} from './types';
import { 
  fileToBase64, 
  simulateTextStreaming, 
  getPlatformContext,
  getFeatureKnowledge
} from './copilotUtils';

// Maximum number of conversations to keep in localStorage
const MAX_CONVERSATIONS = 10;
// Maximum number of messages per conversation for localStorage
const MAX_MESSAGES = 30;
// Maximum number of prompts for free users
const FREE_PROMPT_LIMIT = 5;

export function useAICopilotCore() {
  const isMobile = useIsMobile();
  // Always closed by default on both mobile and desktop (critical fix)
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [conversations, setConversations] = useState<CopilotConversation[]>([]);
  const [currentConversation, setCurrentConversation] = useState<CopilotConversation | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const location = useLocation();
  
  // Reference to track if we should cancel an ongoing response
  const cancelResponseRef = useRef<boolean>(false);

  // Initialize or load existing conversation on mount
  useEffect(() => {
    loadConversations();
    
    // Log initial state for debugging
    console.log('useAICopilotCore initialized:', { 
      isMobile, 
      isOpenInitialValue: false // Always false now as per critical fix
    });
  }, []);

  // Update isOpen when mobile status changes
  useEffect(() => {
    console.log('Mobile status changed to:', isMobile);
    // Ensure it stays closed on any device type change
    setIsOpen(false);
  }, [isMobile]);

  // Load conversations safely from localStorage
  const loadConversations = () => {
    const savedConversations = localStorage.getItem('ai-copilot-conversations');
    
    if (savedConversations) {
      try {
        // Parse and format the timestamps correctly
        const parsed = JSON.parse(savedConversations);
        const formatted = parsed.map((conv: any) => ({
          ...conv,
          createdAt: new Date(conv.createdAt),
          updatedAt: new Date(conv.updatedAt),
          messages: conv.messages.map((msg: any) => ({
            ...msg,
            timestamp: new Date(msg.timestamp)
          })),
          // Ensure promptCount is initialized properly
          promptCount: conv.promptCount || conv.messages.filter((msg: any) => msg.role === 'user').length,
          // Ensure lastUpdated is present
          lastUpdated: conv.lastUpdated || new Date().toISOString()
        }));
        
        setConversations(formatted);
        
        // Set the current conversation to the most recent one
        if (formatted.length > 0) {
          setCurrentConversation(formatted[0]);
        } else {
          createNewConversation();
        }
      } catch (error) {
        console.error("Error parsing saved conversations:", error);
        createNewConversation();
      }
    } else {
      createNewConversation();
    }
  };

  // Save conversations to localStorage with error handling and pruning
  const saveConversations = (conversationsToSave: CopilotConversation[]) => {
    if (!conversationsToSave.length) return;
    
    try {
      // Prune conversations to prevent localStorage quota issues
      const prunedConversations = pruneConversations(conversationsToSave);
      localStorage.setItem('ai-copilot-conversations', JSON.stringify(prunedConversations));
    } catch (error) {
      console.error("Error saving conversations to localStorage:", error);
      
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        // If quota is exceeded, aggressively prune and try again
        try {
          const aggressivelyPrunedConversations = pruneConversations(conversationsToSave, true);
          localStorage.setItem('ai-copilot-conversations', JSON.stringify(aggressivelyPrunedConversations));
          
          // Update state with pruned conversations
          setConversations(aggressivelyPrunedConversations);
          
          toast({
            title: "Storage limit reached",
            description: "Some older conversations have been pruned to save space.",
          });
        } catch (fallbackError) {
          // If aggressive pruning still fails, clear all but current conversation
          console.error("Failed to save even with aggressive pruning:", fallbackError);
          
          const currentOnly = currentConversation ? 
            pruneMessages([currentConversation], MAX_MESSAGES / 2) : 
            [createNewConversation(false)];
            
          try {
            localStorage.setItem('ai-copilot-conversations', JSON.stringify(currentOnly));
            setConversations(currentOnly);
            
            toast({
              title: "Storage limit reached",
              description: "Only the current conversation has been preserved due to storage constraints.",
              variant: "destructive"
            });
          } catch (criticalError) {
            // Last resort: clear storage completely
            localStorage.removeItem('ai-copilot-conversations');
            const freshConversation = createNewConversation(false);
            setConversations([freshConversation]);
            setCurrentConversation(freshConversation);
            
            toast({
              title: "Storage error",
              description: "Unable to save conversations. Storage has been reset.",
              variant: "destructive"
            });
          }
        }
      }
    }
  };

  // Function to prune conversations and messages to fit storage limits
  const pruneConversations = (conversationsToSave: CopilotConversation[], aggressive = false) => {
    // First, limit the number of conversations
    const limitedConversations = conversationsToSave.slice(0, aggressive ? MAX_CONVERSATIONS / 2 : MAX_CONVERSATIONS);
    
    // Then limit the number of messages per conversation
    return pruneMessages(limitedConversations, aggressive ? MAX_MESSAGES / 2 : MAX_MESSAGES);
  };
  
  // Function to prune messages within each conversation
  const pruneMessages = (conversationsToSave: CopilotConversation[], messageLimit: number) => {
    return conversationsToSave.map(conv => {
      // If messages are under the limit, return as is
      if (conv.messages.length <= messageLimit) {
        return conv;
      }
      
      // Otherwise, keep the first and most recent messages
      const keepFirst = Math.floor(messageLimit / 3); // Keep 1/3 from the start
      const keepLast = messageLimit - keepFirst; // Keep the rest from the end
      
      const prunedMessages = [
        ...conv.messages.slice(0, keepFirst),
        ...conv.messages.slice(-keepLast)
      ];
      
      return {
        ...conv,
        messages: prunedMessages,
        isPruned: true
      };
    });
  };

  // Save conversations to localStorage whenever they change
  useEffect(() => {
    if (conversations.length > 0) {
      saveConversations(conversations);
    }
  }, [conversations]);

  // Create a new conversation
  const createNewConversation = (updateState = true) => {
    const newConversation: CopilotConversation = {
      id: Date.now().toString(),
      messages: [],
      createdAt: new Date(),
      updatedAt: new Date(),
      promptCount: 0,
      lastUpdated: new Date().toISOString() // Add the lastUpdated property
    };
    
    if (updateState) {
      setConversations(prev => [newConversation, ...prev]);
      setCurrentConversation(newConversation);
    }
    
    return newConversation;
  };

  // Clear the current conversation
  const clearConversation = () => {
    createNewConversation();
  };

  // Switch to a different conversation
  const switchConversation = (id: string) => {
    const conversation = conversations.find(c => c.id === id);
    if (conversation) {
      setCurrentConversation(conversation);
    }
  };

  // Delete a conversation
  const deleteConversation = (id: string) => {
    // Filter out the conversation to delete
    const updatedConversations = conversations.filter(c => c.id !== id);
    setConversations(updatedConversations);
    
    // If we're deleting the current conversation, switch to the most recent one
    if (currentConversation?.id === id) {
      if (updatedConversations.length > 0) {
        setCurrentConversation(updatedConversations[0]);
      } else {
        // If no conversations left, create a new one
        createNewConversation();
      }
    }

    // Show a toast notification
    toast({
      title: "Conversation deleted",
      description: "The conversation has been removed from your history."
    });
  };

  // Toggle the copilot visibility
  const toggleCopilot = () => {
    console.log("Toggling copilot visibility, current state:", isOpen);
    setIsOpen(prev => !prev);
  };

  // Close the copilot
  const closeCopilot = () => {
    console.log("Closing copilot, was:", isOpen);
    setIsOpen(false);
    
    // Clean up the hash if it was our AI interaction marker
    if (window.location.hash === '#ai-interaction') {
      window.history.replaceState(
        null, 
        '', 
        window.location.pathname + window.location.search
      );
    }
  };

  // Open the copilot
  const openCopilot = () => {
    console.log("Opening copilot, was:", isOpen);
    setIsOpen(true);
  };

  // Update streaming message content
  const updateStreamingMessage = (messageId: string, displayContent: string, isStreaming: boolean) => {
    // Update both the current conversation and the conversations array
    setCurrentConversation(prev => {
      if (!prev) return null;
      
      return {
        ...prev,
        messages: prev.messages.map(msg => {
          if (msg.id === messageId) {
            return {
              ...msg,
              displayContent,
              isStreaming
            };
          }
          return msg;
        })
      };
    });
    
    setConversations(prevConversations => 
      prevConversations.map(conv => {
        if (conv.id === currentConversation?.id) {
          return {
            ...conv,
            messages: conv.messages.map(msg => {
              if (msg.id === messageId) {
                return {
                  ...msg,
                  displayContent,
                  isStreaming
                };
              }
              return msg;
            })
          };
        }
        return conv;
      })
    );
  };
  
  // Send a message to the copilot with improved context awareness
  const sendMessage = async (content: string, attachments: File[] = []) => {
    if (!content.trim() && attachments.length === 0) return;
    
    if (!currentConversation) {
      createNewConversation();
    }
    
    // Reset cancel flag when starting a new request
    cancelResponseRef.current = false;
    
    // Check if the user has reached the free limit
    const currentPromptCount = currentConversation?.promptCount || 0;
    const hasReachedLimit = currentPromptCount >= FREE_PROMPT_LIMIT;
    
    // Skip API call if limit reached but still add user message
    if (hasReachedLimit && content !== "new conversation") {
      // Only show toast on the first attempt after limit
      if (currentPromptCount === FREE_PROMPT_LIMIT) {
        toast({
          title: "Free limit reached",
          description: "You've used all 5 free AI Copilot prompts. Please upgrade to continue.",
          variant: "destructive"
        });
      }
    }
    
    // Process attachments
    const processedAttachments: CopilotAttachment[] = [];
    
    if (attachments.length > 0) {
      try {
        for (const file of attachments) {
          // Convert file to base64 for storage
          const base64Data = await fileToBase64(file);
          
          // Determine the attachment type based on file.type
          let attachmentType: "image" | "document" | "link" = "document"; // Default to document
          if (file.type.startsWith('image/')) {
            attachmentType = "image";
          }
          
          // Add to processed attachments
          processedAttachments.push({
            id: `attachment-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
            name: file.name,
            type: attachmentType, // Use the correctly typed variable
            size: file.size,
            data: base64Data
          });
        }
      } catch (error) {
        console.error("Error processing attachments:", error);
        toast({
          title: "Error",
          description: "Failed to process attachments. Please try again.",
          variant: "destructive"
        });
        return;
      }
    }
    
    // Check if it's a "new conversation" command
    if (content === "new conversation") {
      createNewConversation();
      return;
    }
    
    // Prepare the user message
    const userMessage: CopilotMessage = {
      id: `user-${Date.now()}`,
      content,
      role: 'user',
      timestamp: new Date(),
      attachments: processedAttachments.length > 0 ? processedAttachments : undefined
    };
    
    // Increment prompt count
    const newPromptCount = (currentPromptCount || 0) + 1;
    
    // Update conversations with user message and new prompt count
    setConversations(prevConversations => 
      prevConversations.map(conv => 
        conv.id === currentConversation?.id 
          ? {
              ...conv,
              messages: [...conv.messages, userMessage],
              updatedAt: new Date(),
              promptCount: newPromptCount,
              lastUpdated: new Date().toISOString() // Update lastUpdated
            }
          : conv
      )
    );
    
    // Also update current conversation
    setCurrentConversation(prev => {
      if (!prev) return null;
      return {
        ...prev,
        messages: [...prev.messages, userMessage],
        updatedAt: new Date(),
        promptCount: newPromptCount,
        lastUpdated: new Date().toISOString() // Update lastUpdated
      };
    });

    setIsLoading(true);
    
    // If limit is reached, stop here after adding user message
    if (hasReachedLimit) {
      setIsLoading(false);
      return;
    }
    
    try {
      // Extract potential feature requests from the user's message
      const lowerContent = content.toLowerCase();
      let featureContext = '';
      
      // Check for specific feature-related queries
      if (lowerContent.includes('valuation') || lowerContent.includes('worth') || lowerContent.includes('value of')) {
        featureContext = getFeatureKnowledge('valuation') || '';
      } else if (lowerContent.includes('match') || lowerContent.includes('find investor') || lowerContent.includes('right investor')) {
        featureContext = getFeatureKnowledge('investor-matching') || '';
      } else if (lowerContent.includes('pitch deck') || lowerContent.includes('presentation') || lowerContent.includes('slide')) {
        featureContext = getFeatureKnowledge('pitch-deck') || '';
      } else if (lowerContent.includes('crm') || lowerContent.includes('investor relationship') || lowerContent.includes('track investor')) {
        featureContext = getFeatureKnowledge('crm') || '';
      }
      
      // Get detailed platform context
      const platformContext = getPlatformContext(location);
      
      // Get conversation history context (last 3 exchanges)
      const conversationHistory = currentConversation?.messages
        .slice(-6) // Get last 6 messages (3 exchanges)
        .map(msg => `${msg.role}: ${msg.content}`)
        .join('\n\n');
      
      // Prepare prompt with enhanced context awareness
      let contextualPrompt = `
# CONTEXT INFORMATION
${platformContext}

${featureContext ? `\n# FEATURE-SPECIFIC KNOWLEDGE\n${featureContext}\n` : ''}
      
${conversationHistory ? `\n# RECENT CONVERSATION\n${conversationHistory}\n` : ''}

# USER QUERY
${content}

${processedAttachments.length > 0 ? 
  `\n# ATTACHMENTS\n${processedAttachments.map(att => 
    `[Attachment: ${att.name} (${att.type}, ${Math.round(att.size / 1024)} KB)]`
  ).join('\n')}\n` : ''}
      
As Stevey AI, provide a helpful, specific response based on the context and information above. Be specific to our platform's features and capabilities.
`;
      
      // Call the API
      const response = await callSmartApi(contextualPrompt);
      
      // Check if the response was cancelled
      if (cancelResponseRef.current) {
        // Reset the flag and stop processing
        cancelResponseRef.current = false;
        setIsLoading(false);
        return;
      }
      
      // Prepare the assistant message with streaming flag
      const assistantMessage: CopilotMessage = {
        id: `assistant-${Date.now()}`,
        content: response,
        role: 'assistant',
        timestamp: new Date(),
        isStreaming: true,  // Start in streaming mode
        displayContent: '' // Empty initial display content
      };
      
      // Update conversations with assistant message and lastUpdated
      setConversations(prevConversations => 
        prevConversations.map(conv => 
          conv.id === currentConversation?.id 
            ? {
                ...conv,
                messages: [...conv.messages, assistantMessage],
                updatedAt: new Date(),
                lastUpdated: new Date().toISOString() // Update lastUpdated
              }
            : conv
        )
      );
      
      // Also update current conversation
      setCurrentConversation(prev => {
        if (!prev) return null;
        return {
          ...prev,
          messages: [...prev.messages, assistantMessage],
          updatedAt: new Date(),
          lastUpdated: new Date().toISOString() // Update lastUpdated
        };
      });

      // Start streaming effect for the received response
      simulateTextStreaming(
        assistantMessage.id,
        response,
        updateStreamingMessage,
        () => {
          // Callback when streaming is complete
          console.log("Streaming complete");
          setIsLoading(false);
        },
        // Pass cancel reference to streaming function
        cancelResponseRef
      );
    } catch (error) {
      console.error("Error fetching AI response:", error);
      toast({
        title: "Error",
        description: "Failed to get response from AI. Please try again.",
        variant: "destructive"
      });
      
      // Add error message to conversation
      const errorMessage: CopilotMessage = {
        id: `error-${Date.now()}`,
        content: "Sorry, I encountered an error. Please try again.",
        role: 'assistant',
        timestamp: new Date()
      };
      
      setConversations(prevConversations => 
        prevConversations.map(conv => 
          conv.id === currentConversation?.id 
            ? {
                ...conv,
                messages: [...conv.messages, errorMessage],
                updatedAt: new Date(),
                lastUpdated: new Date().toISOString() // Update lastUpdated
              }
            : conv
        )
      );
      
      setCurrentConversation(prev => {
        if (!prev) return null;
        return {
          ...prev,
          messages: [...prev.messages, errorMessage],
          updatedAt: new Date(),
          lastUpdated: new Date().toISOString() // Update lastUpdated
        };
      });
      
      setIsLoading(false);
    }
  };

  // Function to cancel an ongoing response
  const cancelResponse = () => {
    if (isLoading) {
      cancelResponseRef.current = true;
      toast({
        title: "Response cancelled",
        description: "The current AI response has been cancelled."
      });
    }
  };

  return {
    isOpen,
    toggleCopilot,
    closeCopilot,
    openCopilot,
    currentConversation,
    sendMessage,
    cancelResponse,
    isLoading,
    clearConversation,
    conversations,
    switchConversation,
    deleteConversation,
    createNewConversation
  };
}
